import {
  ArrowLeft,
  ArrowUpRight,
  BookOpen,
  CalendarDays,
  Clock3,
  Download,
  FileText,
  Headphones,
  ListMusic,
  PlayCircle,
  Radio
} from "lucide-react";
import { type MouseEvent as ReactMouseEvent, useEffect, useMemo, useState } from "react";
import { trackEvent } from "./analytics";
import { episodeAnalysis } from "./episode-analysis.generated";
import { episodeTranscripts } from "./episode-transcript.generated";
import { HarnessDashboard } from "./HarnessDashboard";
import { homeData } from "./home-data.generated";
import { podcastAssetIndex } from "./podcast-assets.generated";

type ItemType = (typeof homeData.items)[number];
type PodcastType = {
  id: string;
  title: string;
  feed_title: string;
  priority: string;
  selection_mode: string;
  focus: string[];
  feed_url: string;
  source_url: string;
  detail_path: string;
  episode_count: number;
  selected_episode_count: number;
  latest_episode_title: string;
  latest_pub_date: string;
  latest_page_url: string;
  has_play_count_data: boolean;
  episodes: PodcastEpisodeType[];
};
type PodcastEpisodeType = {
  id: string;
  index: number;
  title: string;
  pub_date: string;
  duration: string;
  duration_label: string;
  selected: boolean;
  confidence: string;
  score: number | null;
  matched_keywords: string[];
  page_url: string;
  audio_url: string;
  play_count: number | null;
  show_notes_text?: string;
  show_notes_source?: string;
  show_notes_links?: { text: string; url: string }[];
  readiness?: {
    audio_status?: string;
    transcript_status?: string;
    transcript_source_kind?: string;
    transcript_record_count?: number;
    transcript_promoted_to_radar?: boolean;
    cn_knowledge_status?: string;
    cn_knowledge_insight_count?: number;
    processing_next_action?: string;
    blocking_reason?: string;
  };
};
type EpisodeAnalysisKey = keyof typeof episodeAnalysis;
type EpisodeAnalysisType = (typeof episodeAnalysis)[EpisodeAnalysisKey];
type EpisodeTranscriptKey = keyof typeof episodeTranscripts;
type EpisodeTranscriptType = (typeof episodeTranscripts)[EpisodeTranscriptKey];
type EpisodeTranscriptSegment = EpisodeTranscriptType["segments"][number];
type PodcastTextAsset = {
  kind: string;
  key: string;
  public_path: string;
  file_name: string;
  content_type: string;
  size_bytes: number;
  sha256: string;
  source_kind: string;
  source_quality: string;
  record_count: number;
  updated_at: string;
};
type PodcastAssetEntry = {
  source_id: string;
  episode_id: string;
  title: string;
  transcript: PodcastTextAsset | null;
  transcript_pdf: PodcastTextAsset | null;
  cn_readable: PodcastTextAsset | null;
  cn_knowledge_json: PodcastTextAsset | null;
};
type EpisodeTranscriptGroup = {
  id: string;
  label: string;
  range: string;
  startLabel: string;
  startOrdinal: number;
  segments: readonly EpisodeTranscriptSegment[];
};

const TRANSCRIPT_GROUP_WINDOW_MS = 5 * 60 * 1000;
const BEGINNER_SOURCE_IDS = ["practical-ai", "ai-a16z", "silicon-valley-101", "42zhangjing"];
const BLOCKED_HOME_REASON_KEYWORDS = new Set(["ai", "a.i.", "agi", "harness", "harness-smoke"]);
const HOME_EXCLUDED_TITLE_PATTERN = /\bagi\b|harness/i;
const podcastAssetsByEpisode = podcastAssetIndex as Record<string, PodcastAssetEntry>;

const typeLabel: Record<ItemType["type"], string> = {
  case: "Case",
  insight: "Insight"
};

type HomeEpisodePick = {
  podcast: PodcastType;
  episode: PodcastEpisodeType;
  podcastPath: string;
  episodePath: string;
  reason: string;
};

function formatTranscriptClock(totalMs: number) {
  const totalSeconds = Math.max(0, Math.floor(totalMs / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const base = `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  return hours > 0 ? `${String(hours).padStart(2, "0")}:${base}` : base;
}

function buildTranscriptGroups(segments: readonly EpisodeTranscriptSegment[]): EpisodeTranscriptGroup[] {
  const groups = new Map<number, EpisodeTranscriptSegment[]>();

  for (const segment of segments) {
    const bucket = Math.floor(segment.start_ms / TRANSCRIPT_GROUP_WINDOW_MS);
    if (!groups.has(bucket)) groups.set(bucket, []);
    groups.get(bucket)?.push(segment);
  }

  let nextOrdinal = 1;
  return [...groups.entries()].map(([bucket, items], index) => {
    const startMs = bucket * TRANSCRIPT_GROUP_WINDOW_MS;
    const endMs = startMs + TRANSCRIPT_GROUP_WINDOW_MS - 1000;
    const startOrdinal = nextOrdinal;
    nextOrdinal += items.length;
    return {
      id: `transcript-group-${index + 1}`,
      label: `第 ${index + 1} 段`,
      range: `${formatTranscriptClock(startMs)} - ${formatTranscriptClock(endMs)}`,
      startLabel: formatTranscriptClock(startMs),
      startOrdinal,
      segments: items
    };
  });
}

export function App() {
  if (window.location.pathname.startsWith("/harness")) return <HarnessDashboard />;
  if (window.location.pathname.startsWith("/podcasts")) {
    const route = podcastRouteFromPath(window.location.pathname);
    if (route.sourceId && route.episodeId) {
      return <PodcastEpisodePage sourceId={route.sourceId} episodeId={route.episodeId} />;
    }
    return route.sourceId ? <PodcastDetailPage sourceId={route.sourceId} /> : <PodcastListPage />;
  }
  return <HomePage />;
}

function HomePage() {
  const podcasts = homeData.podcasts.items as PodcastType[];

  useEffect(() => {
    trackEvent({
      event_name: "page_view",
      surface: "homepage",
      target: "home"
    });
  }, []);

  const episodePicks = useMemo(() => buildHomeEpisodePicks(podcasts), [podcasts]);
  const featuredPick = episodePicks.find((pick) => pick.episode.audio_url) || episodePicks[0];
  const todayEpisodes = episodePicks
    .filter((pick) => pick.episodePath !== featuredPick?.episodePath)
    .slice(0, 3);
  const beginnerPicks = BEGINNER_SOURCE_IDS
    .map((sourceId) => episodePicks.find((pick) => pick.podcast.id === sourceId))
    .filter((pick): pick is HomeEpisodePick => Boolean(pick))
    .slice(0, 2);
  const chinesePodcast = podcasts.find((podcast) => /[\u4e00-\u9fa5]/.test(`${podcast.title} ${podcast.focus.join(" ")}`));
  const featured = featuredPick || todayEpisodes[0] || beginnerPicks[0];

  return (
    <main className="homepageV7">
      <header className="homeV7Header" aria-label="首页导航">
        <a className="homeV7Brand" href="/">
          <Headphones size={28} strokeWidth={2.2} />
          <span>AI 播客精选导航</span>
        </a>
        <nav className="homeV7Nav" aria-label="主要导航">
          <a aria-current="page" href="/">首页</a>
          <a href="#start-paths">按主题</a>
          <a href="/podcasts/">按节目</a>
          <a href="#today-episodes">最新单集</a>
          <a href={chinesePodcast?.detail_path || "/podcasts/"}>中文播客</a>
          <a href="#about-project">关于</a>
        </nav>
      </header>

      <section className="homeV7Hero" aria-label="AI 播客精选首页首屏">
        <div className="homeV7HeroCopy">
          <p className="homeV7Note">我们为你精选好内容</p>
          <h1>
            <span>值得听的</span>
            <span>
              <span className="homeV7NoBreak">AI 播客</span>，
            </span>
            <span>今天从这里开始</span>
          </h1>
          <p className="homeV7Lead">少一点筛选，多听到真正有价值的讨论。</p>
          <div className="homeV7HeroActions">
            <a className="homeV7PrimaryCta" href={featured?.episodePath || "/podcasts/"}>
              <PlayCircle size={20} />
              播放开场推荐
            </a>
            <a className="homeV7TextLink" href="/podcasts/">
              看全部精选
              <ArrowUpRight size={16} />
            </a>
          </div>
        </div>

        {featured ? (
          <FeaturedHomeEpisode pick={featured} />
        ) : null}
      </section>

      <section id="start-paths" className="homeV7StartPaths" aria-label="三种开始方式">
        <a href="#today-episodes">
          <Radio size={34} />
          <span>
            <strong>追前沿</strong>
            <small>{todayEpisodes.length} 期最近更新</small>
          </span>
        </a>
        <a href={chinesePodcast?.detail_path || "/podcasts/"}>
          <Headphones size={34} />
          <span>
            <strong>找中文</strong>
            <small>{chinesePodcast ? `从 ${chinesePodcast.title} 开始` : "查看全部播客"}</small>
          </span>
        </a>
        <a href="#beginner-picks">
          <ListMusic size={34} />
          <span>
            <strong>补基础</strong>
            <small>{beginnerPicks.length} 个入门选择</small>
          </span>
        </a>
      </section>

      <section className="homeV7ListeningGrid" aria-label="精选收听列表">
        <article id="today-episodes" className="homeV7ListBlock">
          <div className="homeV7SectionHead">
            <h2>今日三集</h2>
            <a href="/podcasts/">
              查看全部
              <ArrowUpRight size={15} />
            </a>
          </div>
          <div className="homeV7EpisodeRows" data-testid="homepage-today-episodes">
            {todayEpisodes.map((pick) => (
              <HomeEpisodeRow key={pick.episodePath} pick={pick} />
            ))}
          </div>
        </article>

        <article id="beginner-picks" className="homeV7ListBlock homeV7BeginnerBlock">
          <div className="homeV7SectionHead">
            <h2>适合新手</h2>
            <a href="/podcasts/">
              更多节目
              <ArrowUpRight size={15} />
            </a>
          </div>
          <div className="homeV7BeginnerRows" data-testid="homepage-beginner-picks">
            {beginnerPicks.map((pick) => (
              <BeginnerPickRow key={pick.podcast.id} pick={pick} />
            ))}
          </div>
        </article>
      </section>

      <footer id="about-project" className="homeV7Footer">
        <span>基于 {podcasts.length.toLocaleString("en-US")} 档真实播客和 {homeData.podcasts.total_episodes.toLocaleString("en-US")} 期单集整理。</span>
        <a href={homeData.links.github_repo} target="_blank" rel="noreferrer">
          查看源码
          <ArrowUpRight size={15} />
        </a>
      </footer>
    </main>
  );
}

function FeaturedHomeEpisode({ pick }: { pick: HomeEpisodePick }) {
  return (
    <article
      className="homeV7FeaturedCard"
      data-testid="homepage-featured-episode"
      data-podcast-id={pick.podcast.id}
      data-episode-id={episodeRouteId(pick.episode)}
      data-source="homeData.podcasts.items"
    >
      <PodcastArtwork podcast={pick.podcast} size="large" />
      <div className="homeV7FeaturedCopy">
        <span className="homeV7Tag">开场推荐</span>
        <h2>
          <a data-testid="homepage-episode-link" href={pick.episodePath}>
            {pick.episode.title}
          </a>
        </h2>
        <p>{pick.podcast.title}</p>
        <div className="homeV7MetaLine">
          <span>{pick.episode.duration_label || "暂无时长"}</span>
          <span>{pick.reason}</span>
        </div>
        <a className="homeV7QuietListen" href={pick.episodePath}>
          <PlayCircle size={18} />
          立即收听
        </a>
      </div>
    </article>
  );
}

function HomeEpisodeRow({ pick }: { pick: HomeEpisodePick }) {
  return (
    <article
      className="homeV7EpisodeRow"
      data-testid="homepage-today-episode"
      data-podcast-id={pick.podcast.id}
      data-episode-id={episodeRouteId(pick.episode)}
      data-source="homeData.podcasts.items"
    >
      <PodcastArtwork podcast={pick.podcast} />
      <div className="homeV7RowCopy">
        <h3>
          <a data-testid="homepage-episode-link" href={pick.episodePath}>
            {pick.episode.title}
          </a>
        </h3>
        <p>
          {pick.podcast.title}
          <span>{pick.episode.duration_label || formatDate(pick.episode.pub_date)}</span>
          <em>{pick.reason}</em>
        </p>
      </div>
      <a className="homeV7PlayGhost" href={pick.episodePath} aria-label={`收听 ${pick.episode.title}`}>
        <PlayCircle size={18} />
      </a>
    </article>
  );
}

function BeginnerPickRow({ pick }: { pick: HomeEpisodePick }) {
  return (
    <article
      className="homeV7BeginnerRow"
      data-testid="homepage-beginner-pick"
      data-podcast-id={pick.podcast.id}
      data-episode-id={episodeRouteId(pick.episode)}
      data-source="homeData.podcasts.items"
    >
      <PodcastArtwork podcast={pick.podcast} size="wide" />
      <div>
        <h3>
          <a data-testid="homepage-podcast-link" href={pick.podcastPath}>
            {pick.podcast.title}
          </a>
        </h3>
        <p>{pick.podcast.focus.slice(0, 3).join(" · ") || pick.episode.title}</p>
        <small>{pick.reason}</small>
      </div>
      <a className="homeV7PlayGhost" href={pick.episodePath} aria-label={`收听 ${pick.episode.title}`}>
        <PlayCircle size={18} />
      </a>
    </article>
  );
}

function PodcastArtwork({
  podcast,
  size = "square"
}: {
  podcast: PodcastType;
  size?: "square" | "large" | "wide";
}) {
  return (
    <a
      className={`homeV7Artwork homeV7Artwork-${size} tone-${podcastArtworkTone(podcast.id)}`}
      href={podcast.detail_path || `/podcasts/${podcast.id}/`}
      data-testid="homepage-podcast-link"
      aria-label={`打开 ${podcast.title}`}
    >
      <span>{podcastArtworkLabel(podcast.title)}</span>
    </a>
  );
}

function buildHomeEpisodePicks(podcasts: PodcastType[]): HomeEpisodePick[] {
  return podcasts
    .flatMap((podcast) =>
      podcast.episodes
        .filter((episode) => episode.title && !HOME_EXCLUDED_TITLE_PATTERN.test(episode.title))
        .map((episode) => ({
          podcast,
          episode,
          podcastPath: podcast.detail_path || `/podcasts/${podcast.id}/`,
          episodePath: episodeDetailPath(podcast.id, episode),
          reason: homeRecommendationReason(podcast, episode)
        }))
    )
    .sort((left, right) => {
      const selectedScore = Number(right.episode.selected) - Number(left.episode.selected);
      if (selectedScore !== 0) return selectedScore;
      const byDate = Date.parse(right.episode.pub_date || "") - Date.parse(left.episode.pub_date || "");
      if (Number.isFinite(byDate) && byDate !== 0) return byDate;
      return (right.episode.score || 0) - (left.episode.score || 0);
    });
}

function homeRecommendationReason(podcast: PodcastType, episode: PodcastEpisodeType) {
  const keyword = episode.matched_keywords.find((item) => !BLOCKED_HOME_REASON_KEYWORDS.has(item.toLowerCase()));
  if (keyword) return `关键词：${keyword}`;
  const focus = podcast.focus[0];
  if (focus) return `方向：${focus}`;
  if (episode.duration_label) return `时长：${episode.duration_label}`;
  return `更新：${formatDate(episode.pub_date)}`;
}

function podcastArtworkLabel(title: string) {
  const cleaned = title
    .replace(/podcast/gi, "")
    .replace(/the\s+/gi, "")
    .replace(/[：:|｜].+$/, "")
    .trim();
  const words = cleaned.split(/\s+/).filter(Boolean);
  if (/[\u4e00-\u9fa5]/.test(cleaned)) return cleaned.slice(0, 4);
  return words.slice(0, 3).join(" ") || title.slice(0, 12);
}

function podcastArtworkTone(id: string) {
  const tones = ["ink", "green", "amber", "slate", "paper"];
  const code = [...id].reduce((sum, char) => sum + char.charCodeAt(0), 0);
  return tones[code % tones.length];
}

function HomePodcastCard({ podcast, index }: { podcast: PodcastType; index: number }) {
  const detailPath = podcast.detail_path || `/podcasts/${podcast.id}/`;
  const latestEpisode = podcast.episodes.find((episode) => episode.title) || null;
  const latestTitle = latestEpisode?.title || podcast.latest_episode_title || "暂无可用单集";
  const latestEpisodePath = latestEpisode ? episodeDetailPath(podcast.id, latestEpisode) : "";
  const summary = podcast.focus.length > 0
    ? podcast.focus.slice(0, 5).join(" · ")
    : podcast.feed_title || "这档播客暂时没有简介。";

  return (
    <article
      className="homePodcastCard"
      data-testid="homepage-podcast-card"
      data-podcast-id={podcast.id}
    >
      <div className="homePodcastCardHead">
        <span className="homePodcastRank">{String(index).padStart(2, "0")}</span>
        <h3>
          <a className="homePodcastTitleLink" data-testid="homepage-podcast-link" href={detailPath}>
            {podcast.title}
          </a>
        </h3>
      </div>

      <p className="homePodcastSummary">{summary}</p>

      <div className="homePodcastLatest">
        <span>最新或代表单集</span>
        {latestEpisodePath ? (
          <a data-testid="homepage-episode-link" href={latestEpisodePath}>
            {latestTitle}
            <ArrowUpRight size={15} />
          </a>
        ) : (
          <strong>{latestTitle}</strong>
        )}
        <small>更新于 {formatDate(podcast.latest_pub_date)}</small>
      </div>

      <div className="homePodcastMeta" aria-label={`${podcast.title} 内容概况`}>
        <span>{podcast.episode_count.toLocaleString("en-US")} 期</span>
        <span>{podcast.selected_episode_count.toLocaleString("en-US")} 期已选</span>
        <span>{podcast.has_play_count_data ? "播放量已接入" : "播放量未接入"}</span>
      </div>

      <div className="homePodcastActions">
        <a href={detailPath}>
          播客详情
          <ArrowUpRight size={14} />
        </a>
        {latestEpisodePath ? (
          <a href={latestEpisodePath}>
            单集详情
            <ArrowUpRight size={14} />
          </a>
        ) : null}
      </div>
    </article>
  );
}

function PodcastListPage() {
  const [activeFilter, setActiveFilter] = useState<DirectoryFilter>(() => readDirectoryFilterFromLocation());
  const podcasts = useMemo(() => {
    return [...(homeData.podcasts.items as PodcastType[])].sort(
      (a, b) => new Date(b.latest_pub_date).getTime() - new Date(a.latest_pub_date).getTime()
    );
  }, []);
  const filteredPodcasts = useMemo(() => filterDirectoryPodcasts(podcasts, activeFilter), [activeFilter, podcasts]);
  const episodePicks = useMemo(() => buildDirectoryEpisodePicks(podcasts).slice(0, 6), [podcasts]);
  const chinesePodcasts = useMemo(() => podcasts.filter(isChinesePodcast).slice(0, 4), [podcasts]);
  const transcriptReadyCount = useMemo(() => podcasts.filter((podcast) => countPodcastTranscripts(podcast) > 0).length, [podcasts]);
  const playableCount = useMemo(() => podcasts.filter(hasPodcastAudio).length, [podcasts]);

  useEffect(() => {
    trackEvent({
      event_name: "page_view",
      surface: "podcasts",
      target: "podcast_list"
    });
  }, []);

  useEffect(() => {
    function syncFilterFromHistory() {
      setActiveFilter(readDirectoryFilterFromLocation());
    }

    window.addEventListener("popstate", syncFilterFromHistory);
    return () => window.removeEventListener("popstate", syncFilterFromHistory);
  }, []);

  function updateDirectoryFilter(filter: DirectoryFilter, options: { scrollToList?: boolean } = {}) {
    setActiveFilter(filter);
    pushDirectoryFilterUrl(filter);
    if (options.scrollToList) {
      window.setTimeout(() => {
        document.querySelector(".directoryV2ListPanel")?.scrollIntoView({ block: "start", behavior: "smooth" });
      }, 0);
    }
  }

  return (
    <main className="directoryV2Page" data-testid="podcast-directory-page" data-active-filter={activeFilter}>
      <header className="directoryV2Topbar" aria-label="全部节目页导航">
        <a className="directoryV2Brand" href="/">
          <Headphones size={28} strokeWidth={2.2} />
          <span>AI 播客精选导航</span>
        </a>
        <nav aria-label="页面内导航">
          <a href="/">首页</a>
          <a
            href={directoryFilterHref("recent")}
            aria-current={activeFilter === "recent" ? "page" : undefined}
            onClick={(event) => {
              event.preventDefault();
              updateDirectoryFilter("recent");
            }}
          >
            全部节目
          </a>
          <a href="#directory-recent">最近值得听</a>
          <a
            href={directoryFilterHref("chinese")}
            aria-current={activeFilter === "chinese" ? "page" : undefined}
            onClick={(event) => {
              event.preventDefault();
              updateDirectoryFilter("chinese", { scrollToList: true });
            }}
          >
            中文节目
          </a>
        </nav>
      </header>

      <section className="directoryV2Hero" aria-label="全部节目页介绍">
        <div className="directoryV2HeroCopy">
          <a className="directoryV2BackLink" href="/">
            <ArrowLeft size={16} />
            返回首页
          </a>
          <p className="directoryV2Eyebrow">按节目浏览</p>
          <h1>{directoryHeroTitle(activeFilter)}</h1>
          <p>{directoryHeroDescription(activeFilter, homeData.podcasts.total_sources)}</p>
        </div>
        <div className="directoryV2SignalGrid" aria-label="节目目录概况">
          <div>
            <span>节目</span>
            <strong>{homeData.podcasts.total_sources}</strong>
          </div>
          <div>
            <span>单集</span>
            <strong>{homeData.podcasts.total_episodes.toLocaleString("en-US")}</strong>
          </div>
          <div>
            <span>有逐字稿</span>
            <strong>{transcriptReadyCount}</strong>
          </div>
          <div>
            <span>可播放</span>
            <strong>{playableCount}</strong>
          </div>
        </div>
      </section>

      <section className="directoryV2FilterBar" aria-label="节目筛选">
        {DIRECTORY_FILTERS.map((filter) => (
          <button
            key={filter.id}
            type="button"
            className={activeFilter === filter.id ? "isActive" : ""}
            onClick={() => updateDirectoryFilter(filter.id)}
            data-testid="podcast-directory-filter"
            aria-pressed={activeFilter === filter.id}
          >
            {filter.label}
          </button>
        ))}
      </section>

      {podcasts.length > 0 ? (
        <section className="directoryV2Layout">
          <article className="directoryV2ListPanel" aria-label="节目列表">
            <div className="directoryV2PanelHead">
              <div>
                <p>节目列表</p>
                <h2>{directoryFilterTitle(activeFilter)}</h2>
              </div>
              <span>{filteredPodcasts.length} 档</span>
            </div>
            <div
              className="directoryV2PodcastRows"
              data-testid="podcast-directory-list"
              data-source="homeData.podcasts.items"
            >
              {filteredPodcasts.map((podcast, index) => (
                <DirectoryPodcastRow key={podcast.id} podcast={podcast} rank={index + 1} />
              ))}
            </div>
          </article>

          <aside className="directoryV2Aside" aria-label="节目目录侧栏">
            <section id="directory-recent" className="directoryV2SidePanel" aria-label="最近值得听">
              <div className="directoryV2PanelHead compact">
                <div>
                  <p>最近值得听</p>
                  <h2>先从这些单集开始</h2>
                </div>
              </div>
              <div className="directoryV2EpisodePicks">
                {episodePicks.map((pick, index) => (
                  <DirectoryEpisodePick key={pick.episodePath} pick={pick} rank={index + 1} />
                ))}
              </div>
            </section>

            <section id="directory-cn" className="directoryV2SidePanel" aria-label="中文节目">
              <div className="directoryV2PanelHead compact">
                <div>
                  <p>中文节目</p>
                  <h2>更适合直接收藏</h2>
                </div>
              </div>
              <div className="directoryV2ChineseList">
                {chinesePodcasts.map((podcast) => (
                  <a key={podcast.id} href={podcast.detail_path || `/podcasts/${podcast.id}/`}>
                    <span>{podcast.title}</span>
                    <small>{podcast.latest_episode_title || "暂无最新单集"}</small>
                  </a>
                ))}
              </div>
            </section>
          </aside>
        </section>
      ) : (
        <section className="emptyPodcastState">
          <Radio size={22} />
          <h2>还没有可展示的播客源</h2>
          <p>暂时没有可展示的播客。你可以稍后再回来查看。</p>
        </section>
      )}
    </main>
  );
}

type DirectoryFilter = "recent" | "transcript" | "chinese" | "playable";

const DIRECTORY_FILTERS: { id: DirectoryFilter; label: string }[] = [
  { id: "recent", label: "最近更新" },
  { id: "transcript", label: "有逐字稿" },
  { id: "chinese", label: "中文节目" },
  { id: "playable", label: "可播放" }
];

function directoryFilterHref(filter: DirectoryFilter) {
  return filter === "recent" ? "/podcasts/" : `/podcasts/?filter=${filter}`;
}

function readDirectoryFilterFromLocation(): DirectoryFilter {
  if (typeof window === "undefined") return "recent";
  const filter = new URLSearchParams(window.location.search).get("filter");
  return isDirectoryFilter(filter) ? filter : "recent";
}

function pushDirectoryFilterUrl(filter: DirectoryFilter) {
  if (typeof window === "undefined") return;
  const nextUrl = directoryFilterHref(filter);
  const currentUrl = `${window.location.pathname}${window.location.search}`;
  if (currentUrl === nextUrl) return;
  window.history.pushState({ directoryFilter: filter }, "", nextUrl);
}

function isDirectoryFilter(value: string | null): value is DirectoryFilter {
  return Boolean(value && DIRECTORY_FILTERS.some((filter) => filter.id === value));
}

function DirectoryPodcastRow({ podcast, rank }: { podcast: PodcastType; rank: number }) {
  const detailPath = podcast.detail_path || `/podcasts/${podcast.id}/`;
  const latestEpisode = latestDirectoryEpisode(podcast);
  const latestEpisodePath = latestEpisode ? episodeDetailPath(podcast.id, latestEpisode) : "";
  const latestTitle = latestEpisode?.title || podcast.latest_episode_title || podcast.feed_title || podcast.title;
  const transcriptCount = countPodcastTranscripts(podcast);
  const audioCount = countPodcastAudio(podcast);
  const latestPlayCount = latestEpisode?.play_count ?? null;
  const statusItems = [
    `${podcast.episode_count.toLocaleString("en-US")} 期收录`,
    audioCount > 0 ? `${audioCount.toLocaleString("en-US")} 期可听` : "音频待接入",
    transcriptCount > 0 ? `${transcriptCount.toLocaleString("en-US")} 期可读` : "逐字稿待补",
    latestPlayCount !== null ? `${latestPlayCount.toLocaleString("en-US")} 次播放` : ""
  ];

  return (
    <article
      className="directoryV2PodcastRow"
      aria-label={`${podcast.title} 节目卡`}
      data-testid="podcast-directory-row"
      data-source="homeData.podcasts.items"
      data-podcast-id={podcast.id}
      data-latest-title={latestTitle}
      data-transcript-count={transcriptCount}
      data-audio-count={audioCount}
    >
      <PodcastArtwork podcast={podcast} />
      <div className="directoryV2PodcastMain">
        <div className="directoryV2PodcastTitleLine">
          <span>{String(rank).padStart(2, "0")}</span>
          <h3>
            <a href={detailPath} data-testid="podcast-directory-link">
              {podcast.title}
            </a>
          </h3>
        </div>
        <p>{podcastSummary(podcast)}</p>
        <div className="directoryV2Tags" aria-label={`${podcast.title} 标签`}>
          {podcast.focus.slice(0, 3).map((item) => (
            <span key={item}>{item}</span>
          ))}
        </div>
      </div>

      <div className="directoryV2Latest">
        <span>最新单集</span>
        {latestEpisodePath ? (
          <a href={latestEpisodePath} data-testid="podcast-directory-episode-link">
            {latestTitle}
          </a>
        ) : (
          <strong>{latestTitle || "暂无记录"}</strong>
        )}
        <small>{formatDate(podcast.latest_pub_date)}</small>
      </div>

      <div className="directoryV2Status" aria-label={`${podcast.title} 元信息`}>
        {statusItems.filter(Boolean).map((item) => (
          <span key={item}>{item}</span>
        ))}
      </div>
    </article>
  );
}

function DirectoryEpisodePick({ pick, rank }: { pick: HomeEpisodePick; rank: number }) {
  const transcriptReady = isEpisodeTranscriptAvailable(pick.episode);
  return (
    <article
      className="directoryV2EpisodePick"
      data-testid="podcast-directory-episode-pick"
      data-podcast-id={pick.podcast.id}
      data-episode-id={episodeRouteId(pick.episode)}
    >
      <span>{String(rank).padStart(2, "0")}</span>
      <div>
        <p>{pick.podcast.title}</p>
        <h3>
          <a href={pick.episodePath}>{pick.episode.title}</a>
        </h3>
        <small>
          {pick.episode.duration_label || formatDate(pick.episode.pub_date)} · {transcriptReady ? "可读" : "待补逐字稿"}
        </small>
      </div>
      <a href={pick.episodePath} aria-label={`打开 ${pick.episode.title}`}>
        <PlayCircle size={18} />
      </a>
    </article>
  );
}

function filterDirectoryPodcasts(podcasts: PodcastType[], filter: DirectoryFilter) {
  if (filter === "transcript") return podcasts.filter((podcast) => countPodcastTranscripts(podcast) > 0);
  if (filter === "chinese") return podcasts.filter(isChinesePodcast);
  if (filter === "playable") return podcasts.filter(hasPodcastAudio);
  return podcasts;
}

function directoryFilterTitle(filter: DirectoryFilter) {
  const labels: Record<DirectoryFilter, string> = {
    recent: "最近更新优先",
    transcript: "已有逐字稿的节目",
    chinese: "中文节目",
    playable: "可以直接收听的节目"
  };
  return labels[filter];
}

function directoryHeroTitle(filter: DirectoryFilter) {
  const labels: Record<DirectoryFilter, string> = {
    recent: "全部节目",
    transcript: "可读节目",
    chinese: "中文节目",
    playable: "可听节目"
  };
  return labels[filter];
}

function directoryHeroDescription(filter: DirectoryFilter, totalSources: number) {
  const labels: Record<DirectoryFilter, string> = {
    recent: `从 ${totalSources} 档真实 AI 播客里，按更新、语言和可读状态找到值得长期关注的节目。`,
    transcript: "优先浏览已经有逐字稿的节目，适合先读重点，再决定要不要完整收听。",
    chinese: "把中文 AI、科技和商业访谈节目放在一起，少一点筛选，直接找到值得长期关注的声音。",
    playable: "只看已经接入音频的节目，适合直接点开一档节目继续收听。"
  };
  return labels[filter];
}

function latestDirectoryEpisode(podcast: PodcastType) {
  return [...podcast.episodes]
    .filter((episode) => episode.title)
    .sort((a, b) => Date.parse(b.pub_date || "") - Date.parse(a.pub_date || ""))[0] || null;
}

function buildDirectoryEpisodePicks(podcasts: PodcastType[]): HomeEpisodePick[] {
  return podcasts
    .flatMap((podcast) =>
      podcast.episodes
        .filter((episode) => episode.title && episode.audio_url)
        .map((episode) => ({
          podcast,
          episode,
          podcastPath: podcast.detail_path || `/podcasts/${podcast.id}/`,
          episodePath: episodeDetailPath(podcast.id, episode),
          reason: homeRecommendationReason(podcast, episode)
        }))
    )
    .sort((left, right) => {
      const transcriptScore = Number(isEpisodeTranscriptAvailable(right.episode)) - Number(isEpisodeTranscriptAvailable(left.episode));
      if (transcriptScore !== 0) return transcriptScore;
      const selectedScore = Number(right.episode.selected) - Number(left.episode.selected);
      if (selectedScore !== 0) return selectedScore;
      return Date.parse(right.episode.pub_date || "") - Date.parse(left.episode.pub_date || "");
    });
}

function podcastSummary(podcast: PodcastType) {
  if (podcast.focus.length > 0) return podcast.focus.slice(0, 4).join(" · ");
  return podcast.feed_title || "这档节目暂时没有简介。";
}

function isChinesePodcast(podcast: PodcastType) {
  return /[\u4e00-\u9fff]/.test(`${podcast.title} ${podcast.feed_title} ${podcast.focus.join(" ")}`);
}

function hasPodcastAudio(podcast: PodcastType) {
  return countPodcastAudio(podcast) > 0;
}

function countPodcastAudio(podcast: PodcastType) {
  return podcast.episodes.filter((episode) => Boolean(episode.audio_url) || episode.readiness?.audio_status === "available").length;
}

function countPodcastTranscripts(podcast: PodcastType) {
  return podcast.episodes.filter(isEpisodeTranscriptAvailable).length;
}

function isEpisodeTranscriptAvailable(episode: PodcastEpisodeType) {
  return episode.readiness?.transcript_status === "available" || Boolean(podcastAssetsByEpisode[episode.id]?.transcript);
}

function PodcastDetailPage({ sourceId }: { sourceId: string }) {
  const podcasts = homeData.podcasts.items as PodcastType[];
  const podcast = podcasts.find((item) => item.id === sourceId);
  const episodes = podcast?.episodes ?? [];

  useEffect(() => {
    trackEvent({
      event_name: "page_view",
      surface: "podcast_detail",
      target: sourceId
    });
  }, [sourceId]);

  if (!podcast) {
    return (
      <main className="pageShell podcastPage">
        <section className="emptyPodcastState">
          <a className="backLink" href="/podcasts/">
            <ArrowLeft size={17} />
            返回所有播客
          </a>
          <Radio size={24} />
          <h1>没有找到这个播客</h1>
          <p>这个播客暂时不可用。请从所有播客页重新选择一个来源。</p>
        </section>
      </main>
    );
  }

  const latestEpisode = latestDirectoryEpisode(podcast) || episodes[0];

  return (
    <main
      className="sourceDetailPage"
      data-testid="podcast-source-detail-page"
      data-source="homeData.podcasts.items"
      data-podcast-id={podcast.id}
      data-episode-count={episodes.length}
    >
      <header className="sourceDetailTopbar" aria-label="播客详情页导航">
        <a className="sourceDetailBrand" href="/">
          <Headphones size={28} strokeWidth={2.2} />
          <span>AI 播客精选导航</span>
        </a>
        <nav aria-label="主要导航">
          <a href="/">首页</a>
          <a href="/podcasts/" aria-current="page">
            全部节目
          </a>
          <a href="/podcasts/?filter=chinese">中文节目</a>
        </nav>
      </header>

      <section className="sourceDetailHero" aria-label="播客详情">
        <div className="sourceDetailHeroCopy">
          <a className="sourceDetailBackLink" href="/podcasts/">
            <ArrowLeft size={16} />
            返回全部节目
          </a>
          <p className="sourceDetailEyebrow">节目详情</p>
          <h1>{podcast.title}</h1>
          <p>
            {podcastSummary(podcast)}
            {latestEpisode ? ` 最近可以先看「${latestEpisode.title}」。` : ""}
          </p>
        </div>

        <div className="sourceDetailSignalGrid" aria-label="节目统计">
          <div>
            <span>收录单集</span>
            <strong>{podcast.episode_count.toLocaleString("en-US")}</strong>
          </div>
          <div>
            <span>精选</span>
            <strong>{podcast.selected_episode_count.toLocaleString("en-US")}</strong>
          </div>
          <div>
            <span>最近更新</span>
            <strong>{formatDate(podcast.latest_pub_date)}</strong>
          </div>
          <div>
            <span>浏览顺序</span>
            <strong>最新优先</strong>
          </div>
        </div>
      </section>

      <section className="sourceDetailLayout">
        <aside className="sourceDetailAside" aria-label="节目概览">
          <article className="sourceDetailSidePanel">
            <div className="sourceDetailPanelHead compact">
              <div>
                <p>快速判断</p>
                <h2>适合关注什么</h2>
              </div>
            </div>
            <div className="sourceDetailTags" aria-label={`${podcast.title} 标签`}>
              {podcast.focus.length > 0 ? podcast.focus.slice(0, 6).map((item) => <span key={item}>{item}</span>) : <span>暂无关键词</span>}
            </div>
            <dl className="sourceDetailFacts">
              <div>
                <dt>最新单集</dt>
                <dd>{podcast.latest_episode_title || "暂无记录"}</dd>
              </div>
            </dl>
          </article>

        </aside>

        <article className="sourceEpisodePanel" aria-label="单集列表">
          <div className="sourceDetailPanelHead">
            <div>
              <p>单集列表</p>
              <h2>从最新一期开始浏览</h2>
            </div>
            <span>{episodes.length.toLocaleString("en-US")} 期</span>
          </div>

          {episodes.length > 0 ? (
            <div className="sourceEpisodeList" data-testid="podcast-source-episode-list">
              {episodes.map((episode, index) => (
                <EpisodeRow
                  key={episode.id || episode.index}
                  sourceId={podcast.id}
                  episode={episode}
                  rank={index + 1}
                />
              ))}
            </div>
          ) : (
            <div className="emptyPodcastState compactEmpty">
              <ListMusic size={22} />
              <h2>暂无单集明细</h2>
              <p>这个播客暂时没有可展示的单集。</p>
            </div>
          )}
        </article>
      </section>
    </main>
  );
}

function EpisodeRow({
  sourceId,
  episode,
  rank
}: {
  sourceId: string;
  episode: PodcastEpisodeType;
  rank: number;
}) {
  const detailPath = episodeDetailPath(sourceId, episode);
  const transcriptReady = isEpisodeTranscriptAvailable(episode);

  return (
    <article
      className="sourceEpisodeRow"
      data-testid="podcast-source-episode-row"
      data-episode-id={episodeRouteId(episode)}
      data-title={episode.title}
      data-has-audio={episode.audio_url ? "true" : "false"}
      data-has-transcript={transcriptReady ? "true" : "false"}
      onClick={(event) => {
        const target = event.target instanceof Element ? event.target : null;
        if (target?.closest("a")) return;
        window.location.href = detailPath;
      }}
    >
      <span className="sourceEpisodeRank">{String(rank).padStart(2, "0")}</span>
      <div className="episodeTitleBlock">
        <h3>
          <a className="episodeTitleLink" href={detailPath}>
            {episode.title}
          </a>
        </h3>
        <div className="episodeMeta">
          <span>
            <CalendarDays size={14} />
            {formatDate(episode.pub_date)}
          </span>
          <span>
            <Clock3 size={14} />
            {episode.duration_label || "暂无时长"}
          </span>
          {episode.play_count !== null ? (
            <span>
              <Headphones size={14} />
              {episode.play_count.toLocaleString("en-US")} 次播放
            </span>
          ) : null}
        </div>
      </div>

      <a className="sourceEpisodeOpen" href={detailPath} aria-label={`打开 ${episode.title}`}>
        <ArrowUpRight size={16} />
      </a>
    </article>
  );
}

function PodcastEpisodePage({ sourceId, episodeId }: { sourceId: string; episodeId: string }) {
  const podcasts = homeData.podcasts.items as PodcastType[];
  const podcast = podcasts.find((item) => item.id === sourceId);
  const episode = podcast?.episodes.find((item) => episodeRouteId(item) === episodeId || String(item.index) === episodeId);
  const analysisKey = `${sourceId}/${episodeId}` as EpisodeAnalysisKey;
  const analysis = episodeAnalysis[analysisKey];
  const transcriptKey = `${sourceId}/${episodeId}` as EpisodeTranscriptKey;
  const transcript = episodeTranscripts[transcriptKey];
  const assetEntry = podcastAssetsByEpisode[`${sourceId}/${episodeId}`];
  const transcriptAsset = assetEntry?.transcript || null;
  const transcriptPdfAsset = assetEntry?.transcript_pdf || null;
  const [remoteTranscriptText, setRemoteTranscriptText] = useState("");
  const [remoteTranscriptStatus, setRemoteTranscriptStatus] = useState<"idle" | "loading" | "loaded" | "error">("idle");

  useEffect(() => {
    trackEvent({
      event_name: "page_view",
      surface: "podcast_episode_detail",
      target: `${sourceId}/${episodeId}`
    });
  }, [episodeId, sourceId]);

  useEffect(() => {
    if (!transcriptAsset || transcript) {
      setRemoteTranscriptText("");
      setRemoteTranscriptStatus("idle");
      return;
    }

    let cancelled = false;
    setRemoteTranscriptStatus("loading");
    setRemoteTranscriptText("");

    fetch(transcriptAsset.public_path)
      .then((response) => {
        if (!response.ok) throw new Error(`transcript fetch failed: ${response.status}`);
        return response.text();
      })
      .then((text) => {
        if (cancelled) return;
        setRemoteTranscriptText(text);
        setRemoteTranscriptStatus("loaded");
      })
      .catch(() => {
        if (cancelled) return;
        setRemoteTranscriptText("");
        setRemoteTranscriptStatus("error");
      });

    return () => {
      cancelled = true;
    };
  }, [transcript, transcriptAsset]);

  if (!podcast || !episode) {
    return (
      <main className="pageShell podcastPage podcastReadingPage">
        <section className="emptyPodcastState">
          <a className="backLink" href={podcast ? podcast.detail_path : "/podcasts/"}>
            <ArrowLeft size={17} />
            {podcast ? `返回 ${podcast.title}` : "返回所有播客"}
          </a>
          <ListMusic size={24} />
          <h1>没有找到这期单集</h1>
          <p>这期内容暂时不可用。请返回播客详情页重新选择。</p>
        </section>
      </main>
    );
  }

  const quickFacts = buildEpisodeFactCards(episode, transcript, transcriptAsset);
  const questionCards = buildEpisodeQuestionCards(analysis);
  const showNotesIntro = showNotesParagraphs(episode.show_notes_text || "").slice(0, 3);
  const episodePeople = buildEpisodePeople(episode);
  const episodeLinks = episode.show_notes_links || [];
  const hasGuestCard = episodePeople.length > 0 || episodeLinks.length > 0;
  const transcriptHref = transcriptDownloadHref(transcript, transcriptAsset);
  const transcriptPdfHref = transcriptPdfDownloadHref(transcriptPdfAsset);
  const heroHeadline = episodeHeroHeadline(episode);
  const transcriptAvailable = Boolean(transcript || transcriptAsset);

  return (
    <main className="episodeV4Page">
      <header className="episodeV4Nav" aria-label="播客详情页导航">
        <a className="episodeV4Brand" href="/">
          <Headphones size={26} strokeWidth={2.1} />
          <span>AI 播客精选导航</span>
        </a>
        <nav aria-label="主要导航">
          <a href="/">首页</a>
          <a href="/podcasts/">全部播客</a>
          <a href={podcast.detail_path || `/podcasts/${podcast.id}/`}>返回节目</a>
        </nav>
      </header>

      <article className="episodeV4Shell" data-testid="episode-detail-v4">
        <section className="episodeV4Hero" aria-label="单集概要">
          <div className="episodeV4HeroCopy">
            <a className="episodeV4BackLink" href={podcast.detail_path || `/podcasts/${podcast.id}/`}>
              <ArrowLeft size={16} />
              {podcast.title}
            </a>
            <p className="episodeV4Kicker">本期精选</p>
            <h1>{heroHeadline}</h1>
            <p className="episodeV4Lead">
              {episode.title}。先用几个关键问题判断值不值得听；听完之后，可以带着逐字稿和时间线继续整理。
            </p>
            <div className="episodeV4Meta" aria-label="单集基础信息">
              <span>
                <CalendarDays size={15} />
                {formatDate(episode.pub_date)}
              </span>
              <span>
                <Clock3 size={15} />
                {episode.duration_label || "暂无时长"}
              </span>
              <span>
                <Headphones size={15} />
                {podcast.has_play_count_data && episode.play_count !== null
                  ? `${episode.play_count.toLocaleString("en-US")} 次播放`
                  : "播放量暂未接入"}
              </span>
            </div>
          </div>

          <aside className="episodeV4ListenCard" aria-label="收听和下载">
            <div className="episodeV4ListenHead">
              <PodcastArtwork podcast={podcast} size="large" />
              <div>
                <span>{podcast.title}</span>
                <strong>{episode.duration_label || "可收听"}</strong>
              </div>
            </div>
            {episode.audio_url ? (
              <audio controls preload="metadata" src={episode.audio_url}>
                <a href={episode.audio_url}>打开音频</a>
              </audio>
            ) : (
              <p className="episodeV4AudioMissing">这期暂时没有可播放音频。</p>
            )}
            <div className="episodeV4ListenActions">
              {transcriptHref ? (
                <a className="episodeV4PrimaryAction" href={transcriptHref} download>
                  <Download size={17} />
                  下载 Markdown
                </a>
              ) : null}
              {transcriptPdfHref ? (
                <a href={transcriptPdfHref} download>
                  <Download size={17} />
                  下载 PDF
                </a>
              ) : null}
              {episode.page_url ? (
                <a href={episode.page_url} target="_blank" rel="noreferrer">
                  原始页面
                  <ArrowUpRight size={15} />
                </a>
              ) : null}
              {episode.audio_url ? (
                <a href={episode.audio_url} target="_blank" rel="noreferrer">
                  音频链接
                  <ArrowUpRight size={15} />
                </a>
              ) : null}
            </div>
          </aside>
        </section>

        <section className="episodeV4FactStrip" aria-label="本期速览">
          {quickFacts.map((fact) => (
            <article key={fact.label}>
              <strong>{fact.value}</strong>
              <span>{fact.label}</span>
              <p>{fact.caption}</p>
            </article>
          ))}
        </section>

        <section id="episode-questions" className="episodeV4Section episodeV4QuestionSection" aria-label="本期关键问题">
          <EpisodeV4SectionHeader
            eyebrow="先看这几个问题"
            title="如果这些问题对你有用，这期就值得听"
            body="这里不是把播客拆成普通列表，而是先把你最可能带走的问题摆出来。"
          />
          <div className="episodeV4QuestionGrid">
            {questionCards.map((item, index) => (
              <article key={item.id} className="episodeV4QuestionCard">
                <span>{String(index + 1).padStart(2, "0")}</span>
                <h3>{item.question}</h3>
                <p>{item.answer}</p>
                <a href={item.href}>{item.timeRange}</a>
              </article>
            ))}
          </div>
        </section>

        <section id="episode-show-notes" className="episodeV4Section episodeV4BriefSection" aria-label="节目说明">
          <EpisodeV4SectionHeader
            eyebrow="节目说明"
            title="节目方给出的判断依据"
            body="先读这段，可以判断这期讨论的背景、嘉宾和具体案例是不是你想听的。"
          />
          <div className="episodeV4BriefGrid">
            <div className="episodeV4BriefBody">
              {showNotesIntro.length > 0 ? showNotesIntro.map((paragraph, index) => (
                <p key={`episode-v4-show-note-${index}`}>{paragraph}</p>
              )) : (
                <p>节目方暂未提供可展示的节目说明。</p>
              )}
            </div>
            {hasGuestCard ? (
              <aside className="episodeV4GuestCard" aria-label="嘉宾和延伸阅读">
                {episodePeople.length > 0 ? (
                  <>
                    <h3>本期人物</h3>
                    <ul>
                      {episodePeople.map((person) => (
                        <li key={person}>{person}</li>
                      ))}
                    </ul>
                  </>
                ) : null}
                {episodeLinks.length > 0 ? (
                <>
                  <h3>延伸阅读</h3>
                  <div className="episodeV4LinkList">
                    {episodeLinks.slice(0, 3).map((link) => (
                      <a key={link.url} href={link.url} target="_blank" rel="noreferrer">
                        {link.text}
                        <ArrowUpRight size={14} />
                      </a>
                    ))}
                  </div>
                </>
                ) : null}
              </aside>
            ) : null}
          </div>
        </section>

        {analysis ? (
          <>
            <section id="episode-takeaways" className="episodeV4Section episodeV4TakeawaySection" aria-label="值得带走的内容">
              <EpisodeV4SectionHeader
                eyebrow="值得带走"
                title="听完之后，真正可以拿走的内容"
                body="每条内容都来自当前单集的分析产物，并保留可回听的时间锚点。"
              />
              <div className="episodeV4TakeawayList">
                {analysis.core_insights.map((insight) => (
                  <article key={insight.id} className="episodeV4TakeawayItem">
                    <span>{firstEvidenceTimeLabel(insight.evidence)}</span>
                    <div>
                      <h3>{insight.title}</h3>
                      <p>{insight.summary}</p>
                    </div>
                    <em>{confidenceLabel(insight.confidence)}</em>
                  </article>
                ))}
              </div>
            </section>

            <section id="episode-topic-map" className="episodeV4Section episodeV4TimelineSection" aria-label="本期时间线">
              <EpisodeV4SectionHeader
                eyebrow="回到具体片段"
                title="按时间线找到想听的部分"
                body="适合快速跳听，也适合听过之后回到某个片段继续整理。"
              />
              <div className="episodeV4Timeline">
                {analysis.topic_map.map((topic, index) => (
                  <article key={topic.id} id={`topic-${topic.id}`}>
                    <span>{topic.time_range}</span>
                    <h3>{episodeTimelineTitle(topic.title, index)}</h3>
                  </article>
                ))}
              </div>
            </section>
          </>
        ) : null}

        <section id="episode-transcript-workspace" className="episodeV4Section episodeV4TranscriptIntro" aria-label="逐字稿下载和阅读">
          <div>
            <p className="episodeV4Kicker">逐字稿</p>
            <h2>带走完整文本，再做自己的分析</h2>
            <p>
              这份逐字稿来自已收录文本。它适合下载后放进大语言模型里做总结、对比、提问和二次整理；正式引用前请核对原音频。
            </p>
          </div>
          <div className="episodeV4TranscriptActions">
            {transcriptAvailable ? (
              <>
                <span>
                  <FileText size={16} />
                  {transcriptRecordCount(transcript, transcriptAsset).toLocaleString("en-US")} 段文本
                </span>
                <span>
                  <BookOpen size={16} />
                  {transcriptSourceLabel(transcript, transcriptAsset)}
                </span>
              </>
            ) : (
              <span>
                <FileText size={16} />
                暂无逐字稿
              </span>
            )}
            {transcriptHref ? (
              <a className="episodeV4PrimaryAction" href={transcriptHref} download>
                <Download size={17} />
                下载 Markdown
              </a>
            ) : null}
            {transcriptPdfHref ? (
              <a href={transcriptPdfHref} download>
                <Download size={17} />
                下载 PDF
              </a>
            ) : null}
          </div>
        </section>

        {transcript ? (
          <EpisodeTranscriptPanel transcript={transcript} />
        ) : transcriptAsset ? (
          <RemoteTranscriptPanel asset={transcriptAsset} text={remoteTranscriptText} status={remoteTranscriptStatus} />
        ) : (
          <EpisodeTranscriptUnavailable />
        )}

        <section id="episode-evidence" className="episodeV4Section episodeV4SourceSection" aria-label="来源与核对">
          <EpisodeV4SectionHeader
            eyebrow="来源与核对"
            title="这页用到的都是真实来源"
            body="音频、节目说明、播放量、分析和逐字稿都来自当前数据集，没有使用占位内容。"
          />
          <div className="episodeV4SourceGrid">
            <a href={episode.page_url} target="_blank" rel="noreferrer">
              小宇宙原始页面
              <ArrowUpRight size={15} />
            </a>
            {podcast.source_url ? (
              <a href={podcast.source_url} target="_blank" rel="noreferrer">
                节目主页
                <ArrowUpRight size={15} />
              </a>
            ) : null}
            {transcriptHref ? (
              <a href={transcriptHref} download>
                Markdown 逐字稿
                <Download size={15} />
              </a>
            ) : null}
            {transcriptPdfHref ? (
              <a href={transcriptPdfHref} download>
                PDF 逐字稿
                <Download size={15} />
              </a>
            ) : null}
          </div>
        </section>
      </article>
    </main>
  );
}

function EpisodeV4SectionHeader({
  eyebrow,
  title,
  body
}: {
  eyebrow: string;
  title: string;
  body: string;
}) {
  return (
    <div className="episodeV4SectionHeader">
      <p>{eyebrow}</p>
      <h2>{title}</h2>
      <span>{body}</span>
    </div>
  );
}

function buildEpisodeFactCards(episode: PodcastEpisodeType, transcript?: EpisodeTranscriptType, asset?: PodcastTextAsset | null) {
  const notes = episode.show_notes_text || "";
  const facts: { value: string; label: string; caption: string }[] = [];

  if (/99%/.test(notes)) {
    facts.push({
      value: "99%",
      label: "代码由 AI 完成",
      caption: "来自节目说明中对 Creao 实践的描述"
    });
  }
  if (/3到8次/.test(notes)) {
    facts.push({
      value: "3-8 次",
      label: "每天生产部署",
      caption: "节目说明提到的团队节奏变化"
    });
  }
  if (/六周/.test(notes) && /一天/.test(notes)) {
    facts.push({
      value: "6 周到 1 天",
      label: "产品流程压缩",
      caption: "来自节目说明中的案例表述"
    });
  }
  if (transcript || asset) {
    facts.push({
      value: transcriptRecordCount(transcript, asset).toLocaleString("en-US"),
      label: "逐字稿片段",
      caption: transcriptSourceLabel(transcript, asset)
    });
  }

  return facts.slice(0, 4);
}

function episodeHeroHeadline(episode: PodcastEpisodeType) {
  const notes = episode.show_notes_text || "";
  if (/99%/.test(notes) && /AI/.test(notes)) return "99% 代码由 AI 完成后，团队怎么工作";
  return episode.title;
}

function buildEpisodePeople(episode: PodcastEpisodeType) {
  const fromNotes = extractPeopleFromNotes(episode.show_notes_text || "");
  if (fromNotes.length > 0) return fromNotes.slice(0, 4);
  return extractPeopleFromTitle(episode.title).slice(0, 3);
}

function extractPeopleFromNotes(notes: string) {
  if (!notes.trim()) return [];
  const people: string[] = [];
  const lines = notes.split(/\r?\n/);
  let collectingGuests = false;

  for (const rawLine of lines) {
    const line = rawLine.trim();
    if (!line) continue;

    const specialGuestMatch = line.match(/^Special Guests?:\s*(.+)$/i);
    if (specialGuestMatch) {
      addPeople(people, specialGuestMatch[1]);
      continue;
    }

    const guestHeadingMatch = line.match(/^【嘉宾】\s*(.*)$/);
    const englishGuestMatch = line.match(/^(Guests?|嘉宾)[:：]\s*(.+)$/i);
    if (guestHeadingMatch || englishGuestMatch) {
      collectingGuests = true;
      const inlineText = guestHeadingMatch?.[1] || englishGuestMatch?.[2] || "";
      addPeople(people, inlineText);
      continue;
    }

    if (/^【[^】]+】/.test(line) || /^(OUTLINE|LINKS|CONTACT|DISCLAIMER)[:：]?$/i.test(line)) {
      collectingGuests = false;
    }

    if (collectingGuests) addPeople(people, line);
  }

  return dedupePeople(people);
}

function extractPeopleFromTitle(title: string) {
  const parts = title.split(/\s+[—–-]\s+/);
  if (parts.length < 2) return [];
  const candidate = parts[parts.length - 1].trim();
  if (!candidate || candidate.length > 90) return [];
  if (/\b(podcast|episode|part|recap|roundtable)\b/i.test(candidate)) return [];
  return dedupePeople([candidate.replace(/[.。]+$/, "")]);
}

function addPeople(people: string[], value: string) {
  const cleaned = value
    .replace(/^[-*•]\s*/, "")
    .replace(/^\d+[.:、]\s*/, "")
    .replace(/\s+/g, " ")
    .trim();
  if (!cleaned || cleaned.length > 100) return;
  if (/https?:\/\//i.test(cleaned) || /^\d{1,2}:\d{2}/.test(cleaned)) return;
  if (/^(主播|主持|监制|后期|运营|BGM|你将听到|延伸阅读)/i.test(cleaned)) return;
  people.push(cleaned.replace(/\s+and\s+/gi, " 和 ").replace(/\s*,\s*and\s+/gi, " 和 "));
}

function dedupePeople(people: string[]) {
  const seen = new Set<string>();
  return people.filter((person) => {
    const key = person.toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function buildEpisodeQuestionCards(analysis?: EpisodeAnalysisType) {
  if (!analysis) return [];
  const questions = [
    "这期为什么不只是聊工具？",
    "为什么只买工具还不够？",
    "产品开发变快后，哪里会先卡住？",
    "人还剩下什么关键工作？"
  ];
  const answers = [
    "它聊的是人工智能进入日常工作后，团队流程、责任和信任怎么改变。",
    "嘉宾反复强调，提升效率不是多装助手，而是重新安排工作方式。",
    "当技术团队跑得比市场更快，真正拖慢节奏的可能是沟通、销售理解和组织对齐。",
    "这期给出的答案很直接：定义问题、判断结果、决定要不要信任系统。"
  ];

  return analysis.topic_map.slice(0, 4).map((topic, index) => ({
    id: topic.id,
    question: questions[index] || `${topic.title}意味着什么？`,
    answer: answers[index] || topic.title,
    timeRange: topic.time_range,
    href: `#topic-${topic.id}`
  }));
}

function episodeTimelineTitle(originalTitle: string, index: number) {
  const titles = [
    "他们说的工作流是什么",
    "Creao 的实践为什么引发讨论",
    "智能体怎样帮团队排查和修复问题",
    "产品开发快于市场理解时会发生什么",
    "信任从人转向人工智能，需要哪些边界",
    "人的价值转向定义问题和复核结果"
  ];
  return titles[index] || originalTitle;
}

function firstEvidenceTimeLabel(
  evidence: readonly {
    start_ms: number | null;
  }[]
) {
  const startMs = evidence.find((item) => typeof item.start_ms === "number")?.start_ms;
  return typeof startMs === "number" ? formatTranscriptClock(startMs) : "见来源";
}

function confidenceLabel(value: string) {
  const labels: Record<string, string> = {
    high: "依据较充分",
    medium: "可回听核对",
    low: "需要复核"
  };
  return labels[value] || "可回听核对";
}

function transcriptDownloadHref(transcript?: EpisodeTranscriptType, asset?: PodcastTextAsset | null) {
  if (asset?.public_path) return asset.public_path;
  if (!transcript?.artifact_markdown) return "";
  return `/${transcript.artifact_markdown}`;
}

function transcriptPdfDownloadHref(asset?: PodcastTextAsset | null) {
  return asset?.public_path || "";
}

function transcriptRecordCount(transcript?: EpisodeTranscriptType, asset?: PodcastTextAsset | null) {
  return transcript?.segment_count || asset?.record_count || 0;
}

function transcriptSourceLabel(transcript?: EpisodeTranscriptType, asset?: PodcastTextAsset | null) {
  if (transcript) return transcript.official_transcript_found ? "官方逐字稿" : "已收录逐字稿";
  if (!asset) return "暂无逐字稿";
  if (/official/i.test(`${asset.source_kind} ${asset.source_quality}`)) return "官方逐字稿";
  return "已收录逐字稿";
}

function formatFileSize(bytes: number) {
  if (!Number.isFinite(bytes) || bytes <= 0) return "未知大小";
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024).toLocaleString("en-US")} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function EpisodeQuickSummary({
  episode,
  analysis,
  transcript
}: {
  episode: PodcastEpisodeType;
  analysis?: EpisodeAnalysisType;
  transcript?: EpisodeTranscriptType;
}) {
  const navItems = useMemo(() => [
    {
      href: "#episode-audio",
      label: "收听本期",
      value: episode.audio_url ? "可播放" : "暂不可用"
    },
    {
      href: "#episode-show-notes",
      label: "节目简介",
      value: episode.show_notes_text ? "可阅读" : "暂无"
    },
    {
      href: "#episode-analysis",
      label: "本期要点",
      value: analysis ? `${analysis.core_insights.length} 条` : "暂无"
    },
    {
      href: "#episode-topic-map",
      label: "主题地图",
      value: analysis ? `${analysis.topic_map.length} 段` : "暂无"
    },
    {
      href: "#episode-transcript",
      label: "逐字稿",
      value: transcript ? `${transcript.segment_count.toLocaleString("en-US")} 段` : "暂不可用"
    },
    {
      href: "#episode-evidence",
      label: "来源说明",
      value: analysis ? "可核对" : "基础链接"
    }
  ], [analysis, episode.audio_url, episode.show_notes_text, transcript]);
  const [activeHref, setActiveHref] = useState(navItems[0].href);

  function scrollEpisodeSection(href: string) {
    const target = document.getElementById(href.slice(1));
    if (!target) return false;
    const top = window.scrollY + target.getBoundingClientRect().top - 18;
    const previousScrollBehavior = document.documentElement.style.scrollBehavior;
    document.documentElement.style.scrollBehavior = "auto";
    window.scrollTo(0, Math.max(0, top));
    window.setTimeout(() => {
      document.documentElement.style.scrollBehavior = previousScrollBehavior;
    }, 0);
    window.history.replaceState(null, "", href);
    setActiveHref(href);
    return true;
  }

  useEffect(() => {
    function syncFromHash() {
      const hash = window.location.hash;
      if (navItems.some((item) => item.href === hash)) setActiveHref(hash);
    }

    syncFromHash();
    window.addEventListener("hashchange", syncFromHash);
    return () => window.removeEventListener("hashchange", syncFromHash);
  }, [navItems]);

  useEffect(() => {
    function handleNativeSectionNav(event: globalThis.MouseEvent) {
      const target = event.target instanceof Element ? event.target : null;
      const link = target?.closest(".episodeSectionNav a[href^='#episode-']");
      if (!(link instanceof HTMLAnchorElement)) return;
      if (scrollEpisodeSection(link.hash)) event.preventDefault();
    }

    document.addEventListener("click", handleNativeSectionNav);
    return () => document.removeEventListener("click", handleNativeSectionNav);
  }, []);

  function handleNavClick(event: ReactMouseEvent<HTMLAnchorElement>, href: string) {
    if (scrollEpisodeSection(href)) event.preventDefault();
  }

  return (
    <nav className="episodeQuickSummary episodeSectionNav" aria-label="单集页面导航">
      {navItems.map((item) => (
        <a
          key={item.href}
          href={item.href}
          aria-current={activeHref === item.href ? "location" : undefined}
          onClick={(event) => handleNavClick(event, item.href)}
        >
          <span>{item.label}</span>
          <strong>{item.value}</strong>
        </a>
      ))}
    </nav>
  );
}

function EpisodeAudioPlayer({ episode }: { episode: PodcastEpisodeType }) {
  const hasAudio = Boolean(episode.audio_url);
  return (
    <section id="episode-audio" className="episodeAudioPlayer episodeAudioPanel" aria-label="单集音频播放器">
      <div className="episodeAudioCopy">
        <p className="eyebrow">收听</p>
        <h2>收听本期</h2>
        <p>
          {hasAudio
            ? "可以直接在本页播放，边听边看要点和逐字稿。"
            : "这期暂时没有可播放音频，仍可阅读要点和逐字稿。"}
        </p>
      </div>
      <div className="episodeAudioControl">
        <span className={hasAudio ? "audioStatus available" : "audioStatus unavailable"}>
          {hasAudio ? "音频可播放" : "音频暂不可用"}
        </span>
        {hasAudio ? (
          <>
            <audio controls preload="metadata" src={episode.audio_url}>
              <a href={episode.audio_url}>打开音频</a>
            </audio>
            <a className="audioSourceLink" href={episode.audio_url} target="_blank" rel="noreferrer">
              备用音频链接
              <ArrowUpRight size={15} />
            </a>
          </>
        ) : (
          <p className="audioFallback">请从原文或播客详情页检查该集是否已提供音频链接。</p>
        )}
      </div>
    </section>
  );
}

function EpisodeShowNotesPanel({ episode }: { episode: PodcastEpisodeType }) {
  const paragraphs = showNotesParagraphs(episode.show_notes_text || "");
  const hasShowNotes = paragraphs.length > 0;
  const links = episode.show_notes_links || [];
  return (
    <section id="episode-show-notes" className="episodeArticleSection episodeShowNotesPanel" aria-label="节目简介与 Show Notes">
      <div className="sectionHead">
        <div>
          <p className="eyebrow">节目简介</p>
          <h2>Show Notes</h2>
        </div>
        <span className={hasShowNotes ? "showNotesStatus available" : "showNotesStatus unavailable"}>
          {hasShowNotes ? "源站已提供" : "暂未提供"}
        </span>
      </div>
      {hasShowNotes ? (
        <>
          <div className="showNotesBody">
            {paragraphs.map((paragraph, index) => (
              <p key={`show-note-${index}`}>{paragraph}</p>
            ))}
          </div>
          {links.length > 0 ? (
            <div className="showNotesLinks" aria-label="Show Notes 延伸链接">
              {links.map((link) => (
                <a key={link.url} href={link.url} target="_blank" rel="noreferrer">
                  {link.text}
                  <ArrowUpRight size={14} />
                </a>
              ))}
            </div>
          ) : null}
          <details className="sourceDetails">
            <summary>查看来源说明</summary>
            <p>
              本区块整理自节目公开页面的节目说明；它不是逐字稿。
            </p>
          </details>
        </>
      ) : (
        <p className="showNotesEmpty">
          节目方暂未提供可展示的节目说明；页面会继续保留播放器、关键词和来源链接。
        </p>
      )}
    </section>
  );
}

function showNotesParagraphs(value: string) {
  return value
    .split(/\n{2,}|\n(?=【|[0-9]{1,2}[:：][0-9]{2})/)
    .map((part) => part.trim())
    .filter(Boolean)
    .slice(0, 24);
}

function EpisodeTranscriptUnavailable() {
  return (
    <section id="episode-transcript" className="episodeTranscriptPanel transcriptUnavailable" aria-label="逐字稿暂不可用">
      <div>
        <p className="eyebrow">逐字稿</p>
        <h2>逐字稿暂不可用</h2>
      </div>
      <p className="analysisNotice">
        当前没有可展示的逐字稿，页面会继续保留播放器和本期要点。
      </p>
    </section>
  );
}

function RemoteTranscriptPanel({
  asset,
  text,
  status
}: {
  asset: PodcastTextAsset;
  text: string;
  status: "idle" | "loading" | "loaded" | "error";
}) {
  const loaded = status === "loaded" && text.trim().length > 0;
  return (
    <section id="episode-transcript" className="episodeTranscriptPanel remoteTranscriptPanel" aria-label="逐字稿阅读区">
      <div className="sectionHead">
        <div>
          <p className="eyebrow">逐字稿阅读区</p>
          <h2>逐字稿</h2>
        </div>
        <span className="sectionHint">{asset.record_count.toLocaleString("en-US")} 段</span>
      </div>

      <p className="analysisNotice">
        这份逐字稿已经收录为可下载文本。你可以先在页面里快速阅读，也可以下载后继续整理和分析。
      </p>

      <div className="transcriptGuide" aria-label="逐字稿阅读提示">
        <p>{remoteTranscriptStatusText(status)}</p>
        <div className="transcriptGuideMeta">
          <span>{transcriptSourceLabel(undefined, asset)}</span>
          <span>{formatFileSize(asset.size_bytes)}</span>
          <span>{formatDate(asset.updated_at)}</span>
          <a href={asset.public_path} download>
            下载完整文本
          </a>
        </div>
      </div>

      <details className="sourceDetails transcriptSourceDetails">
        <summary>来源说明</summary>
        <div className="transcriptStatusGrid transcriptReadStatus" aria-label="逐字稿阅读状态">
          <div>
            <span>来源类型</span>
            <strong>{transcriptSourceLabel(undefined, asset)}</strong>
          </div>
          <div>
            <span>片段数</span>
            <strong>{asset.record_count.toLocaleString("en-US")}</strong>
          </div>
          <div>
            <span>文件大小</span>
            <strong>{formatFileSize(asset.size_bytes)}</strong>
          </div>
          <div>
            <span>校验</span>
            <strong>{asset.sha256.slice(0, 12)}</strong>
          </div>
        </div>
        <p>页面只展示可读文本和必要来源说明，不展示本机路径或处理过程中的中间文件。</p>
      </details>

      {loaded ? (
        <pre className="remoteTranscriptText">{text}</pre>
      ) : status === "error" ? (
        <div className="remoteTranscriptState">
          <p>逐字稿暂时加载失败，可以先下载完整文本查看。</p>
          <a href={asset.public_path} download>
            下载完整文本
          </a>
        </div>
      ) : (
        <div className="remoteTranscriptState">
          <p>正在加载逐字稿文本...</p>
        </div>
      )}
    </section>
  );
}

function remoteTranscriptStatusText(status: "idle" | "loading" | "loaded" | "error") {
  if (status === "loaded") return "逐字稿已加载，可以在本页阅读。";
  if (status === "error") return "逐字稿预览暂时没有加载成功，下载入口仍可使用。";
  return "正在从文本资产加载逐字稿。";
}

function EpisodeTranscriptPanel({ transcript }: { transcript: EpisodeTranscriptType }) {
  const transcriptGroups = buildTranscriptGroups(transcript.segments);
  const firstSegmentId = transcriptGroups[0]?.segments[0]?.id;

  function scrollTranscriptTarget(segmentId?: string) {
    if (!segmentId) return false;
    const target = document.getElementById(segmentId);
    if (!target) return false;
    target.scrollIntoView({ block: "start", inline: "nearest" });
    const rect = target.getBoundingClientRect();
    if (rect.top < 0 || rect.top > window.innerHeight * 0.72) {
      window.scrollBy({ top: rect.top - 96, behavior: "auto" });
    }
    window.history.replaceState(null, "", `#${segmentId}`);
    return true;
  }

  function handleTranscriptJump(event: ReactMouseEvent<HTMLAnchorElement>, segmentId?: string) {
    if (scrollTranscriptTarget(segmentId)) event.preventDefault();
  }

  useEffect(() => {
    function handleNativeJump(event: globalThis.MouseEvent) {
      const target = event.target instanceof Element ? event.target : null;
      const link = target?.closest(".episodeTranscriptPanel .transcriptJumpRail a[href^='#asr_segment_']");
      if (!(link instanceof HTMLAnchorElement)) return;
      if (scrollTranscriptTarget(link.hash.slice(1))) event.preventDefault();
    }

    document.addEventListener("click", handleNativeJump);
    return () => document.removeEventListener("click", handleNativeJump);
  }, []);

  return (
    <section id="episode-transcript" className="episodeTranscriptPanel" aria-label="逐字稿阅读区">
      <div className="sectionHead">
        <div>
          <p className="eyebrow">逐字稿阅读区</p>
          <h2>逐字稿</h2>
        </div>
        <span className="sectionHint">{transcript.segment_count.toLocaleString("en-US")} 段</span>
      </div>
      {firstSegmentId ? <span id={firstSegmentId} className="transcriptAnchorMarker" aria-hidden="true" /> : null}

      <p className="analysisNotice">AI 转写可能有误，引用前请核对原音频。</p>

      <div className="transcriptGuide" aria-label="逐字稿阅读提示">
        <p>按时间线分段阅读，先看时间段，再点时间锚点定位到对应片段。</p>
        <div className="transcriptGuideMeta">
          <span>{transcriptGroups.length} 个阅读分段</span>
          <span>每段约 5 分钟</span>
          <span>点击时间可定位</span>
          <span>长句会自动换行</span>
        </div>
      </div>

      <nav className="transcriptJumpRail" aria-label="逐字稿时间导航">
        {transcriptGroups.map((group) => (
          <a key={group.id} href={`#${group.segments[0]?.id}`} onClick={(event) => handleTranscriptJump(event, group.segments[0]?.id)}>
            <strong>{group.startLabel}</strong>
            <span>{group.label}</span>
          </a>
        ))}
      </nav>

      <details className="sourceDetails transcriptSourceDetails">
        <summary>来源说明</summary>
        <div className="transcriptStatusGrid transcriptReadStatus" aria-label="逐字稿阅读状态">
          <div>
            <span>来源类型</span>
            <strong>{transcriptSourceLabel(transcript)}</strong>
          </div>
          <div>
            <span>官方稿</span>
            <strong>{transcript.official_transcript_found ? "已发现" : "未公开"}</strong>
          </div>
          <div>
            <span>片段数</span>
            <strong>{transcript.segment_count.toLocaleString("en-US")}</strong>
          </div>
          <div>
            <span>格式</span>
            <strong>Markdown</strong>
          </div>
        </div>
        <p>
          以下文本整理自公开音频，不是小宇宙官方逐字稿；适合核对上下文。
        </p>
        <p>页面只展示可读文本和必要来源说明，不展示本机路径或处理过程中的中间文件。</p>
      </details>

      <div className="transcriptScroller" aria-label="带时间锚点的逐字稿片段">
        {transcriptGroups.map((group) => (
          <section className="transcriptChapter" key={group.id} aria-labelledby={`${group.id}-title`}>
            <div className="transcriptChapterHead">
              <p>{group.label}</p>
              <h3 id={`${group.id}-title`}>{group.range}</h3>
            </div>
            <div className="transcriptChapterList">
              {group.segments.map((segment, segmentIndex) => (
                <article
                  className="transcriptSegment"
                  key={segment.id}
                  id={segment.id === firstSegmentId ? `${segment.id}-text` : segment.id}
                  aria-label={`逐字稿片段 ${group.startOrdinal + segmentIndex}`}
                >
                  <div className="transcriptTimestampWrap">
                    <span className="transcriptSegmentIndex">片段 {group.startOrdinal + segmentIndex}</span>
                    <a
                      className="transcriptTimestamp"
                      href={`#${segment.id === firstSegmentId ? firstSegmentId : segment.id}`}
                      aria-label={`定位到 ${segment.time_range}`}
                    >
                      {formatTranscriptClock(segment.start_ms)}
                    </a>
                    <time dateTime={segment.time_range}>{segment.time_range}</time>
                  </div>
                  <p>{segment.text}</p>
                </article>
              ))}
            </div>
          </section>
        ))}
      </div>

    </section>
  );
}

function EpisodeAnalysisPanel({ analysis }: { analysis: EpisodeAnalysisType }) {
  return (
    <section id="episode-analysis" className="episodeInsightPanel" aria-label="单集内容分析">
      <div className="sectionHead">
        <div>
          <p className="eyebrow">本期要点</p>
          <h2>这期讲什么</h2>
        </div>
        <span className="sectionHint">{analysis.core_insights.length} 条</span>
      </div>

      <p className="analysisNotice">先看要点，再按时间线回到音频和逐字稿核对。</p>

      <div className="analysisGrid">
        <article id="episode-topic-map" className="analysisBlock episodeTopicMap">
          <h3>主题地图</h3>
          <div className="topicList">
            {analysis.topic_map.map((topic) => (
              <div key={topic.id} className="topicItem">
                <span>{topic.time_range}</span>
                <strong>{topic.title}</strong>
              </div>
            ))}
          </div>
        </article>

        <article className="analysisBlock">
          <h3>核心洞察</h3>
          <div className="insightList">
            {analysis.core_insights.map((insight) => (
              <div key={insight.id} className="insightItem">
                <h4>{insight.title}</h4>
                <p>{insight.summary}</p>
                <details className="evidenceDetails">
                  <summary>查看依据</summary>
                  <small>{insight.evidence.map((item) => item.record_id).join(", ")}</small>
                </details>
              </div>
            ))}
          </div>
        </article>
      </div>

      <div className="analysisGrid compactAnalysisGrid">
        <article className="analysisBlock">
          <h3>听众反馈</h3>
          <ul>
            {analysis.comment_signals.map((signal) => (
              <li key={signal.id}>
                <strong>{signal.title}</strong>
                <span>{signal.summary}</span>
              </li>
            ))}
          </ul>
        </article>

        <article className="analysisBlock">
          <h3>待核实声明</h3>
          <ul>
            {analysis.verification_claims.map((claim) => (
              <li key={claim.id}>
                <strong>需核实</strong>
                <span>{claim.claim}</span>
              </li>
            ))}
          </ul>
        </article>
      </div>

      <details id="episode-evidence" className="sourceDetails artifactBlock">
        <summary>来源说明</summary>
        <div className="transcriptStatusGrid" aria-label="逐字稿状态">
          <div>
            <span>逐字稿</span>
            <strong>{analysis.transcript.official_transcript_found ? "官方逐字稿" : "已收录逐字稿"}</strong>
          </div>
          <div>
            <span>官方稿</span>
            <strong>{analysis.transcript.official_transcript_found ? "已发现" : "未公开"}</strong>
          </div>
          <div>
            <span>片段数</span>
            <strong>{analysis.transcript.transcript_segments.toLocaleString("en-US")}</strong>
          </div>
          <div>
            <span>来源记录</span>
            <strong>{analysis.transcript.source_records.toLocaleString("en-US")}</strong>
          </div>
        </div>
        <p>
          小宇宙公开网页暂时没有可直接展示的逐字稿句子；本页展示已收录文本，并明确标注为非官方稿。
        </p>
        <p>系统保留了来源记录和可下载文本，用于回看和核对；页面不展示内部文件路径。</p>
      </details>
    </section>
  );
}

function podcastRouteFromPath(pathname: string) {
  const parts = pathname.split("/").filter(Boolean);
  if (parts[0] !== "podcasts") return { sourceId: "", episodeId: "" };
  return {
    sourceId: parts[1] ? decodeURIComponent(parts[1]) : "",
    episodeId: parts[2] === "episodes" && parts[3] ? decodeURIComponent(parts[3]) : ""
  };
}

function episodeRouteId(episode: PodcastEpisodeType) {
  return episode.id || String(episode.index);
}

function episodeDetailPath(sourceId: string, episode: PodcastEpisodeType) {
  return `/podcasts/${encodeURIComponent(sourceId)}/episodes/${encodeURIComponent(episodeRouteId(episode))}/`;
}

function selectionModeLabel(value: string) {
  const labels: Record<string, string> = {
    all_ai: "全量 AI",
    keyword: "关键词筛选"
  };
  return labels[value] || value || "未标注";
}

function formatDate(value: string) {
  if (!value) return "暂无日期";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("zh-CN", {
    year: "numeric",
    month: "short",
    day: "numeric"
  });
}
